import { req, reqJson } from '@/apis/https';
import axios from 'axios';
import qs from 'qs';
import { apiServer } from '@/connect';
import store from '@/store';

const authPayload = () => {
	const auth = store.state.settings.auth || {};
	return JSON.stringify({
		payload: {
			id: auth.accessId || '',
			uid: auth.accessUID || '',
			token: auth.accessToken || '',
			exptime: auth.expTime || '',
		},
	});
};

const boardMeetingBase = ogzuid => `/${ogzuid || ''}/staff/board-meeting`;
const statisticsBase = ogzuid => `/${ogzuid || ''}/staff/statistics`;

// 主辦人登入 (customized organizer login)
export const apiOrzLogin = params => {
	return req('post', `/portal/organizer/login/authenticate`, params);
};

// 公開主辦人註冊：form-urlencoded 九欄位、lang、cf-turnstile-response。
// 回傳 status/code/mesg；dbo.registrationStage 為 pending-email，錯誤以 error 識別。
export const apiRegisterOrganizer = params =>
	req('post', '/portal/organizer/account/registration/', params);

// 認證並換密碼：orzuid、token、temporaryPassword、newPassword、confirmPassword。
// 成功 dbo.registrationStage=complete、nextStep=event-setup-and-plan-payment。
export const apiVerifyOrganizerRegistration = params =>
	req('post', '/portal/organizer/account/registration/verify', params);

// 重寄：userid、email、cf-turnstile-response；限流時回傳 retryAfterSeconds。
export const apiResendOrganizerRegistration = params =>
	req('post', '/portal/organizer/account/registration/resend', params);

// 公開忘記密碼：email、lang、cf-turnstile-response。
// 帳號存在與否都回 dbo.recoveryStage=reset-email-sent-if-eligible。
export const apiRequestOrganizerPasswordReset = params =>
	req('post', '/portal/organizer/account/password/forgot', params);

// Email link 重設密碼：orzuid、token、newPassword、confirmPassword、cf-turnstile-response。
// 此 guest API 不使用登入 payload；成功 dbo.recoveryStage=complete。
export const apiResetOrganizerPassword = params =>
	req('post', '/portal/organizer/account/password/reset', params);

// 首次活動設置狀態：dbo.requiresEvent / subscriptionPending 為 boolean。
export const apiLoadOrganizerOnboardingStatus = ogzuid =>
	req('get', `/portal/organizer/${ogzuid}/onboarding/status`);

// 首次活動建檔：十二個 flat form 欄位，日期 yyyy-MM-dd HH:mm:ss。
// HTTP 200；以 status 判斷結果，成功 dbo.eventbasic 不包含 PIN，失敗由 error 識別。
export const apiCreateOrganizerFirstEvent = (ogzuid, params) =>
	req('post', `/portal/organizer/${ogzuid}/onboarding/event`, params);

const organizerAccountBase = ogzuid =>
	'/portal/organizer/' + ogzuid + '/account';

const organizerWatermarkTraceBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/watermark/trace`;

const organizerBulletinBase = ogzuid =>
	'/portal/organizer/' + ogzuid + '/bulletins';

const eventDashboardBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/dashboard`;

const eventAnalyticsBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/analytics`;

const attendeeJourneyBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/attendee-journey`;

const invitationListBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/invitation-list`;

const rsvpSendBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/rsvp-send`;

const rsvpTrackingBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/rsvp-tracking`;

const postEventSurveyBase = evid =>
	`/event/${encodeURIComponent(evid || '')}/surveys`;

const organizerEventSettingsBase = ogzuid =>
	`/portal/organizer/${ogzuid}/eventmgm`;

const organizerEventNotifyBase = ogzuid =>
	`/portal/organizer/${ogzuid}/eventnotify`;

// 浮水印追蹤：僅 ORZ 可在同組織內反查截圖上的登入追蹤碼。
export const apiResolveWatermarkTrace = (evid, tracecode) =>
	req('post', `${organizerWatermarkTraceBase(evid)}/resolve`, {
		tracecode,
	});

// 報名審核：排入審核結果通知；dbo 回傳 requested／queued／skipped／failed／mode／rows。
export const apiEventNotifyReviewResult = (ogzuid, params = {}) =>
	req('post', `${organizerEventNotifyBase(ogzuid)}/review-result`, params);

// 通知記錄：再次寄送給可編輯的主收件人，並可選填單一 CC Email。
// params: evid, msguid, recipientName, recipientEmail, ccEmail
// response.dbo: origMsguid, newMsguid, recipient, recipientName, cc, ccMsguid, msglogCount
export const apiForwardNotificationHistory = (ogzuid, params = {}) =>
	reqJson(`${organizerEventNotifyBase(ogzuid)}/history/forward`, params);

// Topbar：載入目前公開且在有效期間內的平台公告。
export const apiLoadPublishedBulletins = (ogzuid, params = {}) =>
	req('get', organizerBulletinBase(ogzuid) + '/published', { params });

// 資訊面板：載入統計卡片與圖表彙總。
export const apiDashboardOverview = (evid, params = {}) =>
	req('get', `${eventDashboardBase(evid)}/overview`, { params });

// 資訊面板：載入統計數據。
export const apiDashboardStatistics = (evid, params = {}) =>
	req('get', `${eventDashboardBase(evid)}/statistics`, { params });

// 資訊面板：依目前活動的 20 碼 evid 載入 YouEvent365 GA4 訪客統計。
export const apiDashboardPageviews = (evid, params = {}) =>
	req('get', `${eventDashboardBase(evid)}/pageviews`, { params });

// 資訊面板：載入雲端空間使用量。
export const apiDashboardStorage = (evid, params = {}) =>
	req('get', `${eventDashboardBase(evid)}/storage`, { params });

// 資訊面板：載入近期網站事件。
export const apiDashboardRecentEvents = (evid, params = {}) =>
	req('get', `${eventDashboardBase(evid)}/recent-events`, {
		params,
	});

// 統計圖表：載入同組織跨活動摘要與 server-side 活動分頁。
export const apiAnalyticsOverview = (evid, params = {}) =>
	req('get', `${eventAnalyticsBase(evid)}/overview`, { params });

// 統計圖表：載入指定活動近 90 天 GA4 詳細分析。
export const apiAnalyticsGa4 = (evid, params = {}) =>
	req('get', `${eventAnalyticsBase(evid)}/ga4`, { params });

// 與會者旅程：依 register 搜尋目前活動 Attendee，使用 server-side 分頁。
export const apiAttendeeJourneyAttendees = (evid, params = {}) =>
	req('get', `${attendeeJourneyBase(evid)}/attendees`, { params });

// 與會者旅程：以 register.dataid 載入單一 Attendee 的動態節點與事件。
export const apiAttendeeJourneyDetail = (evid, dataid, params = {}) =>
	req(
		'get',
		`${attendeeJourneyBase(evid)}/attendees/${encodeURIComponent(
			dataid || ''
		)}`,
		{ params }
	);

// 邀請名單：依登入 payload 與目前活動載入有效或回收資料。
export const apiInvitationList = (evid, deleted = '0') =>
	req('get', `${invitationListBase(evid)}/list`, {
		params: { deleted },
	});

// 邀請名單：上傳 Excel 97-2003 HSSF .xls。
export const apiImportInvitationList = (evid, formData) =>
	req('post', `${invitationListBase(evid)}/import`, formData);

// 邀請名單：人工新增；受保護欄位由後端建立。
export const apiCreateInvitation = (evid, params = {}) =>
	reqJson(`${invitationListBase(evid)}/create`, params);

// 邀請名單：編輯七個管理欄位，不允許覆寫 dataid 或 scope。
export const apiUpdateInvitation = (evid, objid, params = {}) =>
	reqJson(
		`${invitationListBase(evid)}/${encodeURIComponent(objid || '')}/update`,
		params
	);

// 邀請名單：軟刪除。
export const apiRemoveInvitation = (evid, objid) =>
	req(
		'post',
		`${invitationListBase(evid)}/${encodeURIComponent(objid || '')}/remove`,
		{}
	);

// 邀請名單：載入單筆變更歷程。
export const apiInvitationTimeline = (evid, objid) =>
	req(
		'get',
		`${invitationListBase(evid)}/${encodeURIComponent(
			objid || ''
		)}/timeline`
	);

// 邀請名額：載入目前可用名額、已報名數與 quota rows。
export const apiInvitationQuota = evid =>
	req('get', `${invitationListBase(evid)}/quota`);

// 邀請名額：以 JSON 執行 1–999 的安全 reconciliation。
export const apiUpdateInvitationQuota = (evid, quota) =>
	reqJson(`${invitationListBase(evid)}/quota`, { quota });

// 邀請名額：取得單一受邀編號的個人化 QR PNG blob。
export const apiInvitationQRCode = (evid, objid) =>
	req(
		'get',
		`${invitationListBase(evid)}/quota/${encodeURIComponent(
			objid || ''
		)}/qrcode`,
		{ responseType: 'blob' }
	);

// 邀請名額：串流下載目前有效 quota rows 的 QR ZIP。
export const apiInvitationQRCodesZip = evid =>
	req('get', `${invitationListBase(evid)}/quota/qrcodes.zip`, {
		responseType: 'blob',
		timeout: 600000,
	});

// RSVP 發送：依登入帳號的 parent/child scope 載入邀請名單與寄送狀態。
export const apiRsvpRecipients = evid =>
	req('get', `${rsvpSendBase(evid)}/recipients`);

// RSVP 發送：載入最後一份 durable draft，沒有草稿時回傳活動預設樣板。
export const apiRsvpDraft = evid =>
	req('get', `${rsvpSendBase(evid)}/draft`);

// RSVP 發送：儲存截止時間、主旨與內容；後端會驗證 merge field allowlist。
export const apiSaveRsvpDraft = (evid, params = {}) =>
	reqJson(`${rsvpSendBase(evid)}/draft`, params);

// RSVP 發送：使用後端安全 placeholder 產生預覽，不回傳真實 dataid。
export const apiPreviewRsvp = (evid, params = {}) =>
	reqJson(`${rsvpSendBase(evid)}/preview`, params);

// RSVP 發送：建立 durable batch；呼叫者須先完成最終確認。
export const apiSendRsvp = (evid, params = {}) =>
	reqJson(`${rsvpSendBase(evid)}/send`, params);

// RSVP 發送：重整後重新取得最後一批 durable 寄送結果。
export const apiLatestRsvpBatch = evid =>
	req('get', `${rsvpSendBase(evid)}/batches/latest`);

// RSVP 追蹤：全域統計不受 params 中的搜尋、篩選與分頁影響。
export const apiRsvpTrackingOverview = (evid, params = {}) =>
	req('get', `${rsvpTrackingBase(evid)}/overview`, { params });

// RSVP 追蹤：載入單一授權 invitation 的隱私裁切歷程。
export const apiRsvpTrackingHistory = (evid, objid) =>
	req(
		'get',
		`${rsvpTrackingBase(evid)}/${encodeURIComponent(
			objid || ''
		)}/history`
	);

// RSVP 追蹤：匯出目前搜尋／篩選／排序的所有符合結果。
export const apiExportRsvpTracking = (evid, params = {}) =>
	req('get', `${rsvpTrackingBase(evid)}/export.xls`, {
		params,
		responseType: 'blob',
		timeout: 600000,
	});

// 會後問卷：載入目前活動的有效問卷清單。
export const apiPostEventSurveyList = evid =>
	req('get', postEventSurveyBase(evid));

// 會後問卷：建立問卷主檔，surid 由 backend 配發。
export const apiCreatePostEventSurvey = (evid, params = {}) =>
	reqJson(`${postEventSurveyBase(evid)}/create`, params);

// 會後問卷：更新問卷名稱、說明與開放時間。
export const apiUpdatePostEventSurvey = (evid, surid, params = {}) =>
	reqJson(
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/update`,
		params
	);

// 會後問卷：軟刪除沒有填答結果的問卷。
export const apiRemovePostEventSurvey = (evid, surid) =>
	req(
		'post',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/remove`,
		{}
	);

// 會後問卷：載入指定問卷的題目設定。
export const apiPostEventSurveyQuestions = (evid, surid) =>
	req(
		'get',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/questions`
	);

// 會後問卷：新增題目，queid 與 ansfname 由 backend 配發。
export const apiCreatePostEventSurveyQuestion = (
	evid,
	surid,
	params = {}
) =>
	reqJson(
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/questions/create`,
		params
	);

// 會後問卷：更新指定題目。
export const apiUpdatePostEventSurveyQuestion = (
	evid,
	surid,
	queid,
	params = {}
) =>
	reqJson(
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/questions/${encodeURIComponent(queid || '')}/update`,
		params
	);

// 會後問卷：軟刪除尚未有填答資料的題目。
export const apiRemovePostEventSurveyQuestion = (evid, surid, queid) =>
	req(
		'post',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/questions/${encodeURIComponent(queid || '')}/remove`,
		{}
	);

// 會後問卷：載入指定題目的異動歷程。
export const apiPostEventSurveyQuestionHistory = (evid, surid, queid) =>
	req(
		'get',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/questions/${encodeURIComponent(queid || '')}/history`
	);

// 會後問卷：載入回覆數、完成率與各題統計。
export const apiPostEventSurveyResultsOverview = (evid, surid) =>
	req(
		'get',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/results/overview`
	);

// 會後問卷：以 server-side 分頁載入逐份填答明細。
export const apiPostEventSurveyResults = (evid, surid, params = {}) =>
	req(
		'get',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/results`,
		{ params }
	);

// 會後問卷：用不透明 responseKey 修正一個既有答案。
export const apiUpdatePostEventSurveyAnswer = (
	evid,
	surid,
	responseKey,
	params = {}
) =>
	reqJson(
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/results/${encodeURIComponent(responseKey || '')}/update`,
		params
	);

// 會後問卷：用不透明 responseKey 軟刪除整份回覆。
export const apiRemovePostEventSurveyResponse = (
	evid,
	surid,
	responseKey
) =>
	req(
		'post',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/results/${encodeURIComponent(responseKey || '')}/remove`,
		{}
	);

// 會後問卷：下載 backend 產生的 HSSF .xls 結果矩陣。
export const apiExportPostEventSurveyResults = (
	evid,
	surid,
	params = {}
) =>
	req(
		'get',
		`${postEventSurveyBase(evid)}/${encodeURIComponent(
			surid || ''
		)}/results/export.xls`,
		{
			params,
			responseType: 'blob',
			timeout: 600000,
		}
	);

// RSVP 發送：EventSource 不支援 header，沿用既有通知功能的 payload query 慣例。
export const openRsvpProgress = (evid, taskid, handlers = {}) => {
	const auth = store.state.settings.auth || {};
	const payload = JSON.stringify({
		payload: {
			id: auth.accessId || '',
			uid: auth.accessUID || '',
			ouid: auth.accessOUID || '',
			token: auth.accessToken || '',
			exptime: auth.expTime || '',
		},
	});
	const url =
		apiServer() +
		`${rsvpSendBase(evid)}/progress?taskid=` +
		encodeURIComponent(taskid || '') +
		'&payload=' +
		encodeURIComponent(payload);
	const source = new EventSource(url);
	const parse = value => {
		try {
			return JSON.parse(value);
		} catch (_error) {
			return {};
		}
	};
	const listen = (event, callback, closes = false) => {
		source.addEventListener(event, message => {
			callback?.(parse(message.data));
			if (closes) source.close();
		});
	};

	listen('snapshot', handlers.onSnapshot);
	listen('progress', handlers.onProgress);
	listen('complete', handlers.onComplete, true);
	source.onerror = error => handlers.onError?.(error);

	return {
		raw: source,
		close: () => source.close(),
	};
};

// 活動網站：讀取目前活動的 app.config。
export const apiLoadEventAppConfig = (evid, params = {}) =>
	req('post', `/platform/api/event/${evid}/app.config`, params);

// 活動報表：依 eventbasic.wtid 的 app.config KEY 前綴產出 XLS，半月報表僅傳入受控的 sdate。
export const apiExportEventTemplateReport = (
	ogzuid,
	evid,
	template,
	sdate = ''
) =>
	axios({
		method: 'post',
		url:
			apiServer() +
			`/event/${encodeURIComponent(
				evid || ''
			)}/report/export/${encodeURIComponent(template || '')}`,
		data: qs.stringify({
			ogzuid,
			...(String(sdate || '').trim()
				? { sdate: String(sdate).trim() }
				: {}),
		}),
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
			payload: authPayload(),
		},
		responseType: 'blob',
		timeout: 600000,
	});

// 活動設置：統計卡片
export const apiEventSettingsStats = (ogzuid, params = {}) =>
	req('get', `${organizerEventSettingsBase(ogzuid)}/stats`, {
		params,
	});

// 活動設置：結構化列表查詢
export const apiEventSettingsList = (ogzuid, params = {}) =>
	req('post', `${organizerEventSettingsBase(ogzuid)}/list`, params);

// 活動設置：匯出目前篩選或勾選資料
export const apiExportEventSettings = (ogzuid, params = {}) =>
	req('post', `${organizerEventSettingsBase(ogzuid)}/export`, params);

// 活動設置：載入單一活動
export const apiLoadEventSettings = (ogzuid, evid, params = {}) =>
	req('get', `${organizerEventSettingsBase(ogzuid)}/event/${evid}`, {
		params,
	});

// 活動設置：建立活動
export const apiCreateEventSettings = (ogzuid, params = {}) =>
	req('post', `${organizerEventSettingsBase(ogzuid)}/event/create`, params);

// 活動設置：更新活動
export const apiUpdateEventSettings = (ogzuid, evid, params = {}) =>
	req(
		'post',
		`${organizerEventSettingsBase(ogzuid)}/event/${evid}/update`,
		params
	);

// 活動設置：複製活動與有效場次
export const apiCopyEventSettings = (ogzuid, evid, params = {}) =>
	req(
		'post',
		`${organizerEventSettingsBase(ogzuid)}/event/${evid}/copy`,
		params
	);

// 活動設置：封存或還原活動
export const apiArchiveEventSettings = (ogzuid, evid, flag, params = {}) =>
	req('post', `${organizerEventSettingsBase(ogzuid)}/event/${evid}/archive`, {
		...params,
		flag,
	});

// 活動設置：載入活動歷程
export const apiLoadEventSettingsTimeline = (ogzuid, evid, params = {}) =>
	req('get', `${organizerEventSettingsBase(ogzuid)}/event/${evid}/timeline`, {
		params,
	});

// 活動設置：讀取報名表單設定
export const apiLoadEventRegistrationForm = (ogzuid, evid, params = {}) =>
	req(
		'get',
		`${organizerEventSettingsBase(ogzuid)}/event/${evid}/registration-form`,
		{ params }
	);

// 活動設置：指定或清除報名表單設定
export const apiSaveEventRegistrationForm = (ogzuid, evid, frmid = '') =>
	req(
		'post',
		`${organizerEventSettingsBase(ogzuid)}/event/${evid}/registration-form`,
		{ frmid }
	);

// 活動場次：依目前活動載入有效或封存場次
export const apiListEventSections = (ogzuid, evid, includeArchived = false) =>
	req('get', `${organizerEventSettingsBase(ogzuid)}/event/${evid}/sections`, {
		params: { includeArchived },
	});

// 活動場次：建立
export const apiCreateEventSection = (ogzuid, evid, params = {}) =>
	req(
		'post',
		`${organizerEventSettingsBase(ogzuid)}/event/${evid}/sections/create`,
		params
	);

// 活動場次：更新
export const apiUpdateEventSection = (ogzuid, evid, objid, params = {}) =>
	req(
		'post',
		`${organizerEventSettingsBase(
			ogzuid
		)}/event/${evid}/sections/${objid}/update`,
		params
	);

// 活動場次：封存或還原
export const apiArchiveEventSection = (ogzuid, evid, objid, flag) =>
	req(
		'post',
		`${organizerEventSettingsBase(
			ogzuid
		)}/event/${evid}/sections/${objid}/remove`,
		{ flag }
	);

// 活動場次：載入歷程
export const apiLoadEventSectionTimeline = (ogzuid, evid, objid, params = {}) =>
	req(
		'get',
		`${organizerEventSettingsBase(
			ogzuid
		)}/event/${evid}/sections/${objid}/timeline`,
		{ params }
	);

// 載入目前登入主辦人的安全個人資料、登入資訊與 capability。
export const apiLoadOrganizerAccountProfile = (ogzuid, params) =>
	req('get', organizerAccountBase(ogzuid) + '/profile', params);

// 更新個人資料 allowlist 與 orzlogin.lang；params.data 必須是 JSON 字串。
export const apiUpdateOrganizerAccountProfile = (ogzuid, params) =>
	req('post', organizerAccountBase(ogzuid) + '/profile', params);

// 驗證舊密碼後修改密碼；永不回傳或保存任何密碼／hash。
export const apiChangeOrganizerAccountPassword = (ogzuid, params) =>
	req('post', organizerAccountBase(ogzuid) + '/password', params);

// YouConf 學術研討會投審稿系統服務諮詢送出
// params: { gstname, unitname, email, cell, unitpost, expect_date, expect_scale, message }
export const apiSendYouConfInquiry = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/youconf/inquiry/send`, params);

// 會員大會現場電子報到服務諮詢送出
// params: { gstname, unitname, email, cell, unitpost, expect_date, expect_scale, message }
export const apiSendGeneralAssemblyInquiry = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/inquiry/send`, params);

// 理監事選舉選票與現場開票服務諮詢送出
// params: { gstname, unitname, email, cell, unitpost, expect_date, expect_scale, message }
export const apiSendBoardElectionInquiry = (ogzuid, params) =>
	apiSendGeneralAssemblyInquiry(ogzuid, {
		...(params || {}),
		service_code: 'board-election-ballot',
		service_name: '理監事選舉選票與現場開票',
		expect_date_label: '預計選舉 / 開票日期',
		expect_scale_label: '預計投票規模',
	});

// YouEvent365 現場議案表決系統服務諮詢送出
// params: { gstname, unitname, email, cell, unitpost, expect_date, expect_scale, message }
export const apiSendResolutionVotingInquiry = (ogzuid, params) =>
	apiSendGeneralAssemblyInquiry(ogzuid, {
		...(params || {}),
		service_code: 'resolution-voting',
		service_name: 'YouEvent365 現場議案表決系統',
		expect_date_label: '預計會員大會 / 表決日期',
		expect_scale_label: '預計表決規模',
	});

// 會員大會名冊：列表
export const apiGeneralAssemblyRosterList = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/roster/list`, params);

// 會員大會名冊：可加入聯絡人候選
export const apiGeneralAssemblyRosterCandidates = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/roster/candidates`, params);

// 會員大會名冊：加入聯絡人
export const apiGeneralAssemblyRosterAdd = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/roster/add`, params);

// 會員大會名冊：移除聯絡人註記
export const apiGeneralAssemblyRosterRemove = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/roster/remove`, params);

// 會員大會名冊：重新產生
export const apiGeneralAssemblyRosterRegenerate = (ogzuid, params) =>
	req('post', `/${ogzuid}/staff/general-assembly/roster/regenerate`, params);

// 會員大會名冊：SSE 進度串流。事件名預期為 snapshot|progress|complete|aborted。
export const openGeneralAssemblyRosterProgress = (ogzuid, taskid, handlers) => {
	const handlersObj = handlers || {};
	const auth = store.state.settings.auth || {};
	const payload = JSON.stringify({
		payload: {
			id: auth.accessId || '',
			uid: auth.accessUID || '',
			token: auth.accessToken || '',
			exptime: auth.expTime || '',
		},
	});
	const url =
		apiServer() +
		`/${ogzuid}/staff/general-assembly/roster/progress?taskid=` +
		encodeURIComponent(taskid) +
		'&payload=' +
		encodeURIComponent(payload);
	const es = new EventSource(url);
	const safeJson = data => {
		try {
			return JSON.parse(data);
		} catch (_e) {
			return data;
		}
	};

	es.addEventListener('snapshot', e => {
		if (handlersObj.onSnapshot) handlersObj.onSnapshot(safeJson(e.data));
	});
	es.addEventListener('progress', e => {
		if (handlersObj.onProgress) handlersObj.onProgress(safeJson(e.data));
	});
	es.addEventListener('complete', e => {
		if (handlersObj.onComplete) handlersObj.onComplete(safeJson(e.data));
		es.close();
	});
	es.addEventListener('aborted', e => {
		if (handlersObj.onAborted) handlersObj.onAborted(safeJson(e.data));
		es.close();
	});
	es.onerror = e => {
		if (handlersObj.onError) handlersObj.onError(e);
	};

	return {
		raw: es,
		close: () => {
			try {
				es.close();
			} catch (_e) {
				/* ignore */
			}
		},
	};
};

// 會員大會名冊：報請內政部備查 Excel
export const apiGeneralAssemblyRosterExport = (ogzuid, params) => {
	const auth = store.state.settings.auth || {};
	const payload = JSON.stringify({
		payload: {
			id: auth.accessId || '',
			uid: auth.accessUID || '',
			token: auth.accessToken || '',
			exptime: auth.expTime || '',
		},
	});
	return axios({
		method: 'post',
		url: apiServer() + `/${ogzuid}/staff/general-assembly/roster/export`,
		data: qs.stringify(params || {}),
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
			payload,
		},
		responseType: 'blob',
		timeout: 60000,
	});
};

// 理監事會議文件櫃：列表 / 搜尋
export const apiBoardMeetingList = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/list`, params);

// 理監事會議文件櫃：新增會議主檔
export const apiBoardMeetingCreate = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/create`, params);

// 理監事會議文件櫃：修改會議主檔
export const apiBoardMeetingUpdate = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/update`, params);

// 理監事會議文件櫃：刪除會議主檔
export const apiBoardMeetingRemove = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/remove`, params);

// 理監事會議文件櫃：文件列表
export const apiBoardMeetingDocuments = (ogzuid, bmuid) =>
	req(
		'get',
		`${boardMeetingBase(ogzuid)}/documents?bmuid=${encodeURIComponent(
			bmuid || ''
		)}`
	);

// 理監事會議文件櫃：上傳 / 重新上傳文件
export const apiBoardMeetingUploadDocument = (ogzuid, formData) =>
	req('post', `${boardMeetingBase(ogzuid)}/document/upload`, formData);

// 理監事會議文件櫃：重新索引文件
export const apiBoardMeetingReindexDocument = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/document/reindex`, params);

// 理監事會議文件櫃：刪除文件
export const apiBoardMeetingDeleteDocument = (ogzuid, params) =>
	req('post', `${boardMeetingBase(ogzuid)}/document/delete`, params);

// 理監事會議文件櫃：歷程
export const apiBoardMeetingHistory = (ogzuid, bmuid) =>
	req(
		'get',
		`${boardMeetingBase(ogzuid)}/history?bmuid=${encodeURIComponent(
			bmuid || ''
		)}`
	);

export const boardMeetingDocumentOpenUrl = (ogzuid, docid) =>
	apiServer() +
	`${boardMeetingBase(ogzuid)}/document/open?docid=${encodeURIComponent(
		docid || ''
	)}` +
	'&payload=' +
	encodeURIComponent(authPayload());

export const boardMeetingDocumentDownloadUrl = (ogzuid, docid) =>
	apiServer() +
	`${boardMeetingBase(ogzuid)}/document/download?docid=${encodeURIComponent(
		docid || ''
	)}` +
	'&payload=' +
	encodeURIComponent(authPayload());

export const apiBoardMeetingExport = (ogzuid, params) => {
	return axios({
		method: 'post',
		url: apiServer() + `${boardMeetingBase(ogzuid)}/export`,
		data: qs.stringify(params || {}),
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
			payload: authPayload(),
		},
		responseType: 'blob',
		timeout: 60000,
	});
};

const statisticsEndpointMap = {
	'member-statistics': 'member',
	'dues-income-statistics': 'dues-income',
	'course-registration-statistics': 'course-registration',
	'course-income-statistics': 'course-income',
	'web-visitor-statistics': 'web-visitor',
};

// 資訊中心 / 統計分析：五頁戰情統計圖表
export const apiStatisticsAnalysis = (ogzuid, pageKey, params) => {
	const endpoint = statisticsEndpointMap[pageKey] || pageKey || 'member';
	return req('get', `${statisticsBase(ogzuid)}/${endpoint}`, {
		params: params || {},
	});
};

// 同仁會員積分查詢：搜尋會員
export const apiSearchMemberScoreBoardMembers = (ogzuid, params) =>
	req('get', `/${ogzuid}/staff/member-score-board/members`, params);

// 同仁會員積分查詢：指定會員與日期區間載入累計表 / 明細表
export const apiGetMemberScoreBoard = (ogzuid, mbuid, params) =>
	req('get', `/${ogzuid}/staff/member-score-board/${mbuid}/data`, params);

// 購票人/受票人-需授權功能登入
export const apiDashboard = (evid, orzuid, params) =>
	// eslint-disable-next-line no-undef
	req('get', `/ticketing/staff/${evid}/dashboard?uu=${orzuid}`, params);
//
//
//
//

// 載入活動基本資料與appconfig
export const apiAuthorizeEvent = (azsid, params) =>
	req('post', `/ticketing/app/authorize/event/${azsid}`, params);
// 購票人忘記密碼，寄送重設密碼函(params: {})
export const apiPurchaserResetPasswd = (evid, params) =>
	req('post', `/ticketing/attendee/${evid}/exmember/passwd/reset`, params);

// 購票人重設密碼前，要抓取mbid跟email
export const apiPurchaserProfile = (evid, mbuid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/exmember/passwd/${mbuid}/profile`,
		params
	);

// 票券登記
export const apiPreRegisterSchema = (
	evid,
	frmid,
	schema_type = 'surveyjs-schema',
	params
) =>
	req(
		'get',
		`/ticketing/app/event/${evid}/form/${frmid}/${schema_type}`,
		params
	);

// 負責人登記 schema - 暫時使用與票券登記相同的端點（稍後手動調整）
export const apiOwnerRegisterSchema = (
	evid,
	frmid,
	schema_type = 'surveyjs-schema',
	params
) =>
	req(
		'get',
		`/ticketing/app/event/${evid}/form/${frmid}/${schema_type}`,
		params
	);

// 列出會員所屬的訂單與訂單明細
export const apiMemberOrderTickets = (evid, mbuid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/register/${mbuid}/ordertickets`,
		params
	);

// 列出會員下面全部的門票登記情形(計算`會議票`、`單人房`、`雙人房`等票種`未登記`、`已登記`、`總人數`資訊)
export const apiMemberTicketStatus = (ogzuid, evid, mbuid, params) =>
	req('post', `/ticketing/attendee/${evid}/register/${mbuid}/status`, params);

// 取得門票單筆資料
export const apiGetTicket = (evid, mbuid, dataid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/register/${mbuid}/ticket/${dataid}`,
		params
	);

// 取得負責人門票單筆資料 - 暫時使用與票券登記相同的端點（稍後手動調整）
export const apiGetOwnerTicket = (evid, mbuid, dataid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/register/${mbuid}/ticket/${dataid}`,
		params
	);

// 儲存門票單筆資料
export const apiSaveTicket = (evid, mbuid, dataid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/register/${mbuid}/ticket/${dataid}/save`,
		params
	);

// 列出會員全部的轉讓記錄資料
export const apiMemberTransferRecords = (evid, mbuid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/transfer/${mbuid}/records`,
		params
	);

// 找出單一筆轉讓資料
export const apiMemberTransferOneRecord = (evid, mbuid, transid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/transfer/${mbuid}/onerecord/${transid}`,
		params
	);

// 轉讓門票
export const apiTransferTicket = (evid, mbuid, params) =>
	req(
		'post',
		`/ticketing/attendee/${evid}/transfer/${mbuid}/tickets`,
		params
	);

///

//

//

// 列出會員全部的門票資料
export const apiMemberTickets = (ogzuid, evid, mbuid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/ticketing/${mbuid}/tickets`,
		params
	);

// 列出會員訂單下面全部的門票資料
export const apiOrderTickets = (ogzuid, evid, mbuid, orderuid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/ticketing/${mbuid}/order/${orderuid}/tickets`,
		params
	);

// 取得會員訂單品項下面全部的門票資料
export const apiOrderitemTickets = (ogzuid, evid, mbuid, itemuid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/ticketing/${mbuid}/item/${itemuid}/tickets`,
		params
	);

// 列出會員訂單下面全部的門票登記情形
export const apiMemberOrderTicketStatus = (
	ogzuid,
	evid,
	mbuid,
	orderuid,
	params
) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/ticketing/${mbuid}/order/${orderuid}/status`,
		params
	);

// 取得會員訂單品項下面全部的門票登記情形
export const apiMemberOrderitemTicketStatus = (
	ogzuid,
	evid,
	mbuid,
	itemuid,
	params
) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/ticketing/${mbuid}/item/${itemuid}/status`,
		params
	);

// 取得直銷外部會員資料
export const apiFindExternalMember = (ogzuid, evid, mbid, params) =>
	req(
		'get',
		`/${ogzuid}/${evid}/attendee/regmember/members/find/${mbid}`,
		params
	);

// 檢查直銷外部會員是否已經報名登記過
export const apiIsMemberRegistered = (ogzuid, evid, dataid, params) =>
	req(
		'get',
		`/${ogzuid}/${evid}/attendee/ticketing/isregistered/${dataid}`,
		params
	);

//
//
//
//
//

// 與會者登入
export const apiSignupLogin = (ogzuid, evid, logintype, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/signup/validation/${logintype}`,
		params
	);

// 與會者首次設定密碼/重設密碼(params: { pincode, email })
export const apiSignupReset = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/passwd/signup/reset/${regno}/${dataid}`,
		params
	);

// 與會者忘記密碼，寄送重設密碼函(params: {})
export const apiSignupForget = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/passwd/signup/forget/${regno}/${dataid}`,
		params
	);

// 與會者忘記密碼，先驗證商務帳號取得signup(params: { pid: pid})
export const apiSignupFetch = (ogzuid, evid, params) =>
	req('post', `/${ogzuid}/${evid}/attendee/signup/fetch`, params);

// 與會者忘記密碼，先驗證商務帳號取得exmblogin(params: { mbid: mbid})
export const apiMemberFetch = (ogzuid, evid, params) =>
	req('post', `/${ogzuid}/${evid}/attendee/exmember/fetch`, params);

// 取得與會者報到與預約時段/候補取號的整合資料
export const apiSignupLoad = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/signup/load/${regno}/${dataid}`,
		params
	);

// 載入報到資料
export const apiValidSignup = (azsid, dataid, params) =>
	req('get', `/app/authorize/signup/${azsid}/${dataid}/valid`, params);

// 載入會員資料
export const apiValidMember = (azsid, mbuid, params) =>
	req('get', `/app/authorize/exmember/${azsid}/${mbuid}/valid`, params);

// 取得預約時段基本資料與預約即時數字
export const apiBookingList = (ogzuid, evid, params) =>
	req('post', `/${ogzuid}/${evid}/attendee/booking/list`, params);
// 預約時段
export const apiBookingCreate = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/booking/create/${regno}/${dataid}`,
		params
	);
// 取消預約
export const apiBookingCancel = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/booking/cancel/${regno}/${dataid}`,
		params
	);

// 現場取號單一櫃位
export const apiWaitingCounter = (ogzuid, evid, params) =>
	req('post', `/${ogzuid}/${evid}/attendee/waiting/get`, params);

// 現場取號
export const apiWaitingCreate = (ogzuid, evid, regno, dataid, params) =>
	req(
		'post',
		`/${ogzuid}/${evid}/attendee/waiting/create/${regno}/${dataid}`,
		params
	);

// 取得現場櫃檯叫號進度
export const apiProgressStatus = (ogzuid, evid, params) =>
	req('post', `/${ogzuid}/${evid}/attendee/progress/status`, params);
